import { getFragmentData, type FragmentType } from '@packages/gql/generated/shopping';
import { LastSeenDocument } from '@packages/gql/generated/shopping/LastSeenDocument';
import { useSession } from '@packages/shared/src/hooks/useSession/useSession';
import { useEffect } from 'react';
import { useMutation } from 'urql';
import { UseAddLastSeenFragmentFragmentDoc } from '@packages/gql/generated/shopping/UseAddLastSeenFragmentFragmentDoc';

/* GraphQL */ `
  fragment UseAddLastSeenFragment on Product {
    orderNumber
  }
`;

/* GraphQL */ `
  mutation LastSeen($productId: String!) {
    addLastSeenProduct(productId: $productId) {
      id
    }
  }
`;

export type UseLastSeenProps = FragmentType<typeof UseAddLastSeenFragmentFragmentDoc>;

/**
 * The hook triggers the `addLastSeenProduct` mutation,
 * which subsequently transfers the provided orderNumber to the LastSeen backend.
 * In this backend, the orderNumber is stored for the particular customer using the JWT token.
 * @param data - the data that is sent to the lastSeen service
 * @returns null
 */
export const useAddLastSeen = (data: UseLastSeenProps) => {
  const { jwtToken } = useSession();
  const [, lastSeenMutation] = useMutation(LastSeenDocument);
  const { orderNumber } = getFragmentData(UseAddLastSeenFragmentFragmentDoc, data);

  // should be called once per DV view
  useEffect(() => {
    if (jwtToken && orderNumber) {
      lastSeenMutation(
        { productId: orderNumber },
        {
          fetchOptions: () => ({
            headers: { 'x-ec-token': jwtToken },
          }),
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber, jwtToken]);

  return null;
};
